import { FC, useEffect, useMemo, useState } from 'react';
import Modal from '@components/Modal';
import { Document } from '@components/DocumentForm';
import { nanoid } from 'nanoid';
import Button from '@components/DesignLibrary/Button';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { DatePicker, Select, Input, Checkbox, TextArea, Currency } from '@/components/DesignLibrary/Atoms';
import * as format from '@format';
import { IncomeMetadata } from '@documentsApi/document-api-models';
import { format as formatDateFns, startOfMonth, isEqual, isAfter, subMonths, sub } from 'date-fns';

import { CurrencyType, CurrencyValue } from '@/typings/api-models';
import Trash from "@components/Icons/Trash";
import Spacer from '@/components/Spacer';
import s from './AddIncomeSourceModal.module.scss';

type Props = {
  onClose: () => void;
  documentRequestUserId: string;
  refetchFiles: () => Promise<void>;
  refetchConsolidatedData: () => Promise<void>;
  isTableViewOpen: boolean;
};

export const incomeSourceOptions=[
  {
    key: 'Employment',
    label: 'Employment',
  },
  {
    key: 'Social Security',
    label: 'Social Security',
  },
  {
    key: 'Rental Property income',
    label: 'Rental Property income',
  },
  {
    key: 'Interest & Dividends',
    label: 'Interest & Dividends',
  },
  {
    key: 'Disability',
    label: 'Disability',
  },
  {
    key: 'Unemployment',
    label: 'Unemployment',
  },
  {
    key: 'Retirement/Pension',
    label: 'Retirement/Pension',
  },
  {
    key: 'Government assistance (welfare, food stamps, etc.)',
    label: 'Government assistance (welfare, food stamps, etc.)',
  },
  {
    key: 'Child support or alimony received',
    label: 'Child support or alimony received',
  },
  {
    key: 'Other',
    label: 'Other',
  },
];

const AddIncomeSourceModal: FC<Props> = ({ onClose, documentRequestUserId, refetchFiles, isTableViewOpen, refetchConsolidatedData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [document, setDocument] = useState<Document>({
    description: '',
    id: nanoid(),
    integrationFileName: null,
    integrationSlug: null,
    internalDataPromptHelp: null,
    internalDataType: null,
    isCollapsed: false,
    isForYourEyesOnly: false,
    isRequired: true,
    metadata: null,
    order: 1,
    title: '',
  });

  const getMonthsInDateRange = (startDate: Date, endDate: Date): string[] => {
    const dates: string[] = [];
    let current = startOfMonth(startDate);
    const end = startOfMonth(endDate);

    while (isAfter(current, end) || isEqual(current, end)) {
      dates.push(formatDateFns(current, 'yyyy-MM-01'));
      current = subMonths(current, 1);
    }

    return dates;
  };

  const lastSixMonths = useMemo(() => getMonthsInDateRange(new Date(), sub(new Date(),{months:5})),
    []);

  const getInitialMonthlyDollarValue = (): IncomeMetadata['monthlyDollarValue'] => lastSixMonths.map(month => ({
    income: { amount: 0, currency: 'USD' as CurrencyType, text: null },
    month,
    useInCalculation: true,
    useInMeansTest: true,
  }));

  const [incomeSource, setIncomeSource] = useState<IncomeMetadata['incomeSource']>('Employment');
  const [monthlyDollarValue, setMonthlyDollarValue] = useState<IncomeMetadata['monthlyDollarValue']>(getInitialMonthlyDollarValue());
  const [SameMonthlyDollarValue, setSameMonthlyDollarValue] = useState<boolean>(true);
  const [description, setDescription] = useState<string| null>(null);
  const [frequency, setFrequency] = useState<IncomeMetadata['payFrequency'] | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isEndDateRequired, setIsEndDateRequired] = useState(true);
  const [isCtaDisabled, setIsCtaDisabled] = useState(true);

  const { getData: addFileFn } = useNoodleApi(tsClient.documentRequests.createDocumentRequestUserFile);

  const resetFields =(): void => {
    setMonthlyDollarValue(getInitialMonthlyDollarValue());
    setDescription(null);
    setFrequency(null);
    setStartDate(null);
    setEndDate(null);
    setIsEndDateRequired(true);
  };

  const handleMonthlyDollarValueChange = (month?: string) => (e: CurrencyValue) => {
    if(!SameMonthlyDollarValue && month) {
      setMonthlyDollarValue(prev =>
        prev.map(item =>
          item.month === month ? { ...item, income: e, month } : item,
        ),
      );
    } else {
      setMonthlyDollarValue(lastSixMonths.map(eachMonth => ({ income: e, month: eachMonth, useInCalculation: true, useInMeansTest:true })));
    }
  };

  const handleAddMonth = (): void => {
    setMonthlyDollarValue(prev => {
      let nextMonth;
      if (prev.length) {
        nextMonth = formatDateFns(subMonths(prev[prev.length - 1].month, 1), 'yyyy-MM-01');
      } else {
        nextMonth = formatDateFns(new Date(), 'yyyy-MM-01');
      }
      return [...prev, { income: { amount: 0, currency: 'USD' as CurrencyType, text: null }, month: nextMonth, useInCalculation:true, useInMeansTest:true }];
    });
  };

  useEffect(() => {
    if (incomeSource==='Employment' && startDate && frequency) {
      setDocument(prev => ({
        ...prev,
        description: `${frequency ? `Pay Frequency: ${frequency}; ` : ''}${
          startDate ? `Start Date: ${format.datetimeLocalTZ.dateString(startDate)}` : ''
        }${(() => {
          if (isEndDateRequired) {
            if (endDate) {
              return `, End Date: ${format.datetimeLocalTZ.dateString(endDate)}`;
            }
            return '';
          }
          return ', still work there';
        })()}`,
        metadata: {
          endDate: endDate && isEndDateRequired ? format.datetimeLocalTZ.dateString(endDate) : null,
          incomeSource,
          monthlyDollarValue:[],
          payFrequency: frequency,
          startDate: format.datetimeLocalTZ.dateString(startDate),
        },
      }));
    }else if(incomeSource!=='Employment') {
      let count=0;
      const totalMonthlyIncome = monthlyDollarValue.reduce((acc, item) => {
        if(item.income && item.income.amount && typeof item.income.amount === 'number') {
          count +=1;
          return acc + item.income.amount;
        }
        return acc;
      }, 0);
      const avarageMonthlyIncome = count ? (totalMonthlyIncome / count).toFixed(2) : '0.00';
      setDocument(prev => ({
        ...prev,
        description: description ? `${description} - Average monthly amount: $ ${avarageMonthlyIncome}`: `Average monthly amount: $ ${avarageMonthlyIncome}`,
        metadata: {
          endDate: null,
          incomeSource,
          monthlyDollarValue,
          payFrequency: 'Monthly',
          startDate: null,
        },
      }));
    }
  }, [startDate, endDate, isEndDateRequired, frequency, incomeSource, description, monthlyDollarValue]);

  useEffect(() => {
    const emptyDoc = {
      description: '',
      id: nanoid(),
      integrationFileName: null,
      integrationSlug: null,
      internalDataPromptHelp: null,
      internalDataType: null,
      isCollapsed: false,
      isForYourEyesOnly: false,
      isRequired: true,
      metadata: null,
      order: 1,
      title: '',
    };
    if(incomeSource==='Employment') {
      setDocument(emptyDoc);
    }
    else setDocument({...emptyDoc, isRequired: false, title: incomeSource});

    resetFields();
  },[incomeSource]);

  // controls enabling/disabling of CTA
  useEffect(() => {
    if (incomeSource === 'Employment') {
      setIsCtaDisabled(!document.title || !frequency);
      return;
    }
    const totalMonthlyDollarValue = monthlyDollarValue.reduce((acc, { income }) => {
      if (income && income.amount && typeof income.amount === 'number') {
        return acc + income.amount;
      }
      return acc;
    }, 0);
    setIsCtaDisabled(totalMonthlyDollarValue < 1);
  }, [incomeSource, document.title, frequency, monthlyDollarValue]);

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    await addFileFn({
      ...document,
      id: documentRequestUserId,
    });
    await refetchFiles();
    if ( isTableViewOpen ) {
      await refetchConsolidatedData();
    }
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Modal hideBottomInset title="Add Income Source" onClose={onClose}>
      <div className={s.container}>
        <Select
          label="Income Source"
          value={incomeSource}
          onChange={val => setIncomeSource(val as IncomeMetadata['incomeSource'])}
          options={incomeSourceOptions}
        />
        {incomeSource==='Employment' && <>
          <Input
            value={document.title}
            onChange={val =>
              setDocument(prev => ({
                ...prev,
                title: val,
              }))
            }
            label="Employer name"
          />
          <Select
            label="Pay frequency"
            value={frequency}
            onChange={val => setFrequency(val as IncomeMetadata['payFrequency'])}
            options={[
              {
                key: 'Weekly',
                label: 'Weekly',
              },
              {
                key: 'Biweekly',
                label: 'Biweekly',
              },
              {
                key: 'Monthly',
                label: 'Monthly',
              },
              {
                key: 'Quarterly',
                label: 'Quarterly',
              },
              {
                key: 'Annually',
                label: 'Annually',
              },
            ]}
          />
          <DatePicker label="Start date" value={startDate} onChange={val => setStartDate(val)} />
          <Checkbox label="Still work there" isChecked={!isEndDateRequired} onChange={e => setIsEndDateRequired(!e)} />
          {isEndDateRequired && <DatePicker label="End date" value={endDate} onChange={val => setEndDate(val)} />}
        </>}

        {
          incomeSource==='Other'
          && <TextArea
            value={description}
            onChange={val =>
              setDescription(val)
            }
            label="Description"
          />
        }

        {incomeSource!=='Employment'
        && <div>
          <Checkbox label="Same value of income for all months"
            isChecked={SameMonthlyDollarValue}
            onChange={e => {
              setSameMonthlyDollarValue(e);
              setMonthlyDollarValue(getInitialMonthlyDollarValue()); }} />

          {SameMonthlyDollarValue
            ?<Currency label='Monthly dollar value' value={monthlyDollarValue.length && monthlyDollarValue[0]?.income || null}
              onChange={handleMonthlyDollarValueChange()}
              isEditor={true}
            />
            : <>
              <Spacer/>
              <p className='body-sm-bold'>Enter income details</p>
              <Spacer/>
              {!!monthlyDollarValue.length && <div className={s['monthly-income-list-header']}>
                <span className={s['month-col-sub-header']}>Month</span>
                <span className={s['income-col-sub-header']}>Income</span>
              </div>}
              {monthlyDollarValue.map((item, idx) =>
                <div className={s['income-list-container']} key={idx}>
                  <div className={s['month-col']} >
                    <DatePicker onChange={() => null} label='' value={item.month} showMonthYearPicker dateFormat='MMM yyyy' />
                  </div>
                  <div className={s['income-col']} >
                    <Currency label='' value={item.income || null}
                      onChange={handleMonthlyDollarValueChange(item.month)}
                      isEditor={true}
                    />
                    <div className={s['delete-btn']} onClick={() => {
                      setMonthlyDollarValue(prev => prev.filter((_, index) => index !== idx));
                    }
                    }>
                      <Trash
                      />
                      <Spacer size={5}/>
                    </div>
                  </div>
                </div>,
              )}
              <Button variant="secondary" size="sm" onClick={handleAddMonth}>
            Add Month
              </Button>
            </>
          }
        </div>
        }

        <Button loading={isSubmitting} disabled={isCtaDisabled} variant="primary" size="md" fullWidth onClick={handleSubmit}>
          Add Income Source
        </Button>
      </div>
    </Modal>
  );
};

export default AddIncomeSourceModal;
